import { ui } from "../lib/localization";
import { Camera, Download, Maximize2 } from "lucide-react";
import { useEffect, useState } from "react";
import { translations, type Language } from "../i18n";
import { formatElapsedDuration } from "../lib/time";
import type { BrowserScreenshot } from "../types";
import { fileAvailability, isEmbeddedResourceUrl } from "../lib/resource-cache";

interface BrowserScreenshotGalleryProps {
  screenshots?: BrowserScreenshot[];
  language: Language;
  messageCreatedAt?: string;
}

export function BrowserScreenshotGallery({ screenshots = [], language, messageCreatedAt }: BrowserScreenshotGalleryProps) {
  const t = translations[language];
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!screenshots.some((image) => !isEmbeddedResourceUrl(image.dataUrl))) return;
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, [screenshots]);
  if (!screenshots.length) return null;

  return (
    <div className="generated-image-grid browser-screenshot-grid">
      {screenshots.map((screenshot, index) => {
        const local = isEmbeddedResourceUrl(screenshot.dataUrl);
        const expiry = screenshot.expiresAt ?? (messageCreatedAt && Number.isFinite(Date.parse(messageCreatedAt)) ? new Date(Date.parse(messageCreatedAt) + 86_400_000).toISOString() : undefined);
        const availability = fileAvailability(local ? undefined : expiry, language, now);
        const expired = !local && availability.expired;
        return (
        <figure className="generated-image browser-screenshot" key={screenshot.id}>
          {expired ? <p className="attachment-error">{availability.label}</p> : <img loading="lazy" decoding="async" width={screenshot.width} height={screenshot.height} src={screenshot.dataUrl} alt={`${t.browserScreenshot}: ${screenshot.title}`} />}
          <figcaption>
            <span>
              <strong>
                <Camera size={13} />
                Browser Run
                {screenshot.elapsedMs != null ? (
                  <em>{t.screenshotDuration(formatElapsedDuration(screenshot.elapsedMs, language))}</em>
                ) : null}
              </strong>
              <small>
                {screenshot.fullPage ? t.screenshotFullPage : screenshot.viewport === "mobile" ? t.screenshotMobile : t.screenshotDesktop}
                {` · ${screenshot.width}×${screenshot.height} · ${screenshot.url}`}
              </small>
              <small>{!local && !expiry ? ui(language, "Temporary screenshot, not saved locally") : availability.label}</small>
            </span>
            <span className="generated-image-actions">
              <a
                href={expired ? undefined : screenshot.dataUrl}
                aria-disabled={expired ? true : undefined}
                target="_blank"
                rel="noreferrer"
                aria-label={t.openScreenshot}
                title={t.openScreenshot}
              ><Maximize2 size={15} /></a>
              <a
                href={expired ? undefined : screenshot.dataUrl}
                aria-disabled={expired ? true : undefined}
                download={`neurondeck-web-${index + 1}.png`}
                aria-label={t.downloadScreenshot}
                title={t.downloadScreenshot}
              ><Download size={15} /></a>
            </span>
          </figcaption>
        </figure>
      ); })}
    </div>
  );
}
